/**
 * System Design Interview Framework — six-step solving template.
 *
 * Engineer-authored, code-bundle content (zero DB reads). This is the
 * Framework_Template referenced by the requirements: a standard, repeatable
 * six-step structure a learner follows for every HLD problem, and mirrored
 * one-to-one by the HLD Workspace tabs/editors.
 *
 * The order of `frameworkSteps` is FIXED and significant (Req 3.1):
 *   (1) Requirements, (2) Estimations, (3) APIs and Data Model,
 *   (4) High-Level Design, (5) Deep Dive, (6) Tradeoffs.
 *
 * Each step provides a name, a purpose description of at least one sentence,
 * and one or more guiding questions the learner is expected to answer in that
 * step (Req 3.2).
 */

/** A single step of the six-step system-design framework. */
export interface FrameworkStep {
 /** Stable identifier for the step (used for keys, routing, workspace mapping). */
 id: string;
 /** Human-readable step name, e.g. "Requirements". */
 name: string;
 /** 1-based position in the fixed framework order (1–6). */
 order: number;
 /** Purpose of the step — at least one full sentence (Req 3.2). */
 purpose: string;
 /** Guiding questions the learner should answer in this step — one or more (Req 3.2). */
 questions: string[];
}

/**
 * The six framework steps in their fixed, required order (Req 3.1).
 * Do not reorder — the workspace editors/panels map to these positions.
 */
export const frameworkSteps: FrameworkStep[] = [
 {
  id: 'requirements',
  name: 'Requirements',
  order: 1,
  purpose:
   'Clarify exactly what the system must do and how well it must do it before designing anything, separating the functional requirements (the features and use cases) from the non-functional requirements (the qualities such as scale, availability, latency, and consistency). Getting alignment here prevents you from solving the wrong problem and frames every decision that follows.',
  questions: [
   'Who are the users of the system and what are the core use cases we must support?',
   'What are the functional requirements — the specific features the system must provide?',
   'What are the non-functional requirements — availability, latency, consistency, durability, and security targets?',
   'What is explicitly out of scope for this design, and what assumptions are we making?',
  ],
 },
 {
  id: 'estimations',
  name: 'Estimations',
  order: 2,
  purpose:
   'Produce back-of-the-envelope numbers that size the system so the architecture is grounded in reality rather than guesswork. Estimating traffic, storage, and bandwidth early reveals which parts of the system are hard and where the bottlenecks will be.',
  questions: [
   'What is the expected number of daily active users, and what read/write ratio do we assume?',
   'What is the peak queries-per-second (QPS) for reads and writes?',
   'How much storage will we accumulate per day, per year, and over the retention period?',
   'What network bandwidth and cache size do these numbers imply?',
  ],
 },
 {
  id: 'apis-and-data-model',
  name: 'APIs and Data Model',
  order: 3,
  purpose:
   'Define the external contract of the system (the API endpoints or RPCs clients call) and the data it stores (entities, relationships, and schema) so that behavior and persistence are precisely specified. A clear API and data model turn vague requirements into concrete interfaces the rest of the design can build on.',
  questions: [
   'What are the core API endpoints or RPCs, including their inputs, outputs, and error cases?',
   'What are the primary entities and how do they relate to one another?',
   'Which storage technology fits each entity (relational, key-value, document, blob), and why?',
   'What are the primary keys, indexes, and access patterns the schema must support?',
  ],
 },
 {
  id: 'high-level-design',
  name: 'High-Level Design',
  order: 4,
  purpose:
   'Sketch the overall architecture — the major components (clients, services, data stores, caches, queues, CDNs) and how requests flow between them — to show how the system satisfies the requirements end to end. This is the backbone diagram that everything else refines.',
  questions: [
   'What are the major components and what responsibility does each one own?',
   'How does a typical read request and a typical write request flow through the system?',
   'Where do we place load balancers, caches, queues, and databases, and why?',
   'How do the components communicate, and where are the boundaries between services?',
  ],
 },
 {
  id: 'deep-dive',
  name: 'Deep Dive',
  order: 5,
  purpose:
   'Zoom into the one or two hardest or most interesting parts of the design and work them out in detail, addressing scale, correctness, and failure handling for the components that make this problem non-trivial. Interviewers use the deep dive to gauge real depth beyond a generic architecture.',
  questions: [
   'Which component is the hardest or most critical, and how do we scale it?',
   'How do we handle partitioning/sharding, replication, and hot spots for that component?',
   'How does the system behave under failures, and how do we recover?',
   'What consistency, ordering, or concurrency guarantees does this part require, and how do we enforce them?',
  ],
 },
 {
  id: 'tradeoffs',
  name: 'Tradeoffs',
  order: 6,
  purpose:
   'Explicitly justify the key design choices by naming the alternatives considered and the tradeoffs accepted, showing that the design is a deliberate set of decisions rather than an accident. Articulating tradeoffs demonstrates engineering maturity and invites the interviewer to probe your reasoning.',
  questions: [
   'What were the main alternatives for each key decision, and why did we choose this one?',
   'What tradeoffs are we accepting — for example consistency versus availability, or cost versus latency?',
   'What are the current bottlenecks and how would the design evolve as scale grows?',
   'What would we monitor, and how would we know the system is healthy or degrading?',
  ],
 },
];

export default frameworkSteps;
